import styled, { css, keyframes } from 'styled-components/macro';

export const LogsContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 500px;
`;

export const LogsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 12px 12px 0 0;
`;

export const LogsTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

export const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

export const FilterField = styled.div`
  width: 160px;
  ${({ $wide }) =>
    $wide &&
    css`
      width: 220px;
    `}
`;

export const TableWrapper = styled.div`
  flex: 1;
  overflow: auto;
`;

export const PaginationWrapper = styled.div`
  padding: 12px 20px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
`;

export const PayloadViewer = styled.div`
  background: #0b1220;
  color: #e7e9ef;
  padding: 16px;
  border-radius: 10px;
  max-height: 460px;
  overflow-y: auto;
`;

export const PayloadPre = styled.pre`
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
`;

// Flow View Styles - Enhanced Visual Design
export const FlowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-height: 70vh;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px 8px 8px 4px;
`;

export const FlowHeader = styled.h3`
  margin: 0;
  font-size: 14px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;

  &::before {
    content: '';
    width: 4px;
    height: 18px;
    background: linear-gradient(135deg, #3b82f6, #8b5cf6);
    border-radius: 2px;
    flex-shrink: 0;
  }
`;

export const SummaryCard = styled.div`
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #bae6fd;
  border-radius: 16px;
  padding: 20px 24px;
  position: relative;
  overflow: hidden;
  flex-shrink: 0;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 150px;
    height: 150px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
  }
`;

export const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 10px;
  border: 1px solid rgba(59, 130, 246, 0.1);
  min-width: 0;

  span:first-child {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
    font-weight: 500;
  }

  span:last-child {
    font-size: 13px;
    font-weight: 600;
    color: #0f172a;
    word-break: break-all;
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const FlowStats = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  flex-shrink: 0;
`;

export const StatBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;

  ${({ $type }) => {
    switch ($type) {
      case 'total':
        return css`
          background: #f1f5f9;
          color: #475569;
        `;
      case 'success':
        return css`
          background: #dcfce7;
          color: #166534;
        `;
      case 'error':
        return css`
          background: #fee2e2;
          color: #991b1b;
        `;
      case 'warning':
        return css`
          background: #fef3c7;
          color: #92400e;
        `;
      case 'duration':
        return css`
          background: #e0e7ff;
          color: #3730a3;
        `;
      default:
        return css`
          background: #f1f5f9;
          color: #475569;
        `;
    }
  }}

  i {
    font-size: 14px;
  }
`;

const pulseAnimation = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
`;

export const FlowTimeline = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding-left: 40px;

  &::before {
    content: '';
    position: absolute;
    left: 15px;
    top: 24px;
    bottom: 24px;
    width: 3px;
    background: linear-gradient(to bottom, #3b82f6, #8b5cf6, #ec4899);
    border-radius: 2px;
  }
`;

export const FlowStep = styled.div`
  position: relative;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '${({ $stepNumber }) => $stepNumber || ''}';
    position: absolute;
    left: -40px;
    top: 16px;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${({ $levelColor }) => $levelColor || '#3b82f6'};
    color: white;
    font-size: 11px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 4px ${({ $levelColor }) => `${$levelColor}25` || 'rgba(59, 130, 246, 0.15)'},
      0 4px 12px ${({ $levelColor }) => `${$levelColor}40` || 'rgba(59, 130, 246, 0.25)'};
    z-index: 2;
  }

  ${({ $isLast }) =>
    $isLast &&
    css`
      &::before {
        animation: ${pulseAnimation} 2s ease-in-out infinite;
      }
    `}
`;

export const StepCard = styled.div`
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: ${({ $levelColor }) => $levelColor || '#3b82f6'};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateX(4px);
  }
`;

export const StepHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: ${({ $levelBg }) => $levelBg || '#f8fafc'};
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;

  &:hover {
    background: ${({ $levelBgHover }) => $levelBgHover || '#f1f5f9'};
  }
`;

export const StepHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const StepNumber = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
`;

export const StepLevelBadge = styled.span`
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ $levelColor }) => `${$levelColor}20` || '#3b82f620'};
  color: ${({ $levelColor }) => $levelColor || '#3b82f6'};
`;

export const StepTimestamp = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-family: 'SF Mono', 'Monaco', monospace;
`;

export const StepTitle = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

export const StepMessage = styled.div`
  padding: 12px 16px;
  font-size: 13px;
  color: #1f2937;
  line-height: 1.5;
  border-bottom: 1px solid #f1f5f9;
`;

export const StepMeta = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #4b5563;
`;

export const StepBodyWrapper = styled.div`
  max-height: ${({ $expanded }) => ($expanded ? '300px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

export const StepBody = styled.pre`
  margin: 0;
  background: #0f172a;
  padding: 12px 16px;
  font-size: 11px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
  color: #e2e8f0;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', monospace;
  max-height: 300px;
  overflow-y: auto;
`;

export const StepToggle = styled.button`
  background: none;
  border: none;
  padding: 8px 16px;
  font-size: 11px;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    color: #3b82f6;
  }

  i {
    font-size: 16px;
    transition: transform 0.2s ease;
    transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0)')};
  }
`;

export const ConnectorArrow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 0;
  margin-left: -24px;

  i {
    color: #cbd5e1;
    font-size: 20px;
  }
`;

export const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 12px;
  border: 2px dashed #e5e7eb;

  i {
    font-size: 48px;
    color: #d1d5db;
    margin-bottom: 12px;
    display: block;
  }
`;

export const InlineActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;

export const IconButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $disabled }) => ($disabled ? '#f3f4f6' : 'rgba(40, 132, 255, 0.12)')};
  color: ${({ $disabled }) => ($disabled ? '#a1a1aa' : 'var(--primary)')};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease-in-out;
  box-shadow: ${({ $disabled }) => ($disabled ? 'none' : '0 8px 18px rgba(15, 23, 42, 0.12)')};
  font-size: 18px;

  &:hover {
    background: ${({ $disabled }) => ($disabled ? '#f3f4f6' : 'rgba(40, 132, 255, 0.22)')};
  }
`;
